import {
  fetchExcelData,
  fetchMedicalHome,
  getMedicalRefundList,
  uploadExel,
} from '@/api';
import FlexWrap from '@/components/common/FlexWrap';
import ImagePreviewModal from '@/components/common/ImagePreviewModal';
import LoadingView from '@/components/common/LoadingView';
import ScrollToTopBtn from '@/components/common/ScrollToTopBtn';
import ExcelTable from '@/components/MedicalReceipt/ExcelTable';
import ReceiptLayout from '@/components/MedicalReceipt/ReceiptLayout';
import { routes } from '@/constant/routes.const';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  IExcelBody,
  IFormattedExcel,
  IUploadExcelImagePayload,
} from '@/types/common.type';
import {
  IMedicalHomePayload,
  IMedicalHomeResponse,
} from '@/types/medical.type';
import { ReceiptSummary } from '@repo/components';
import { convertComponentToImage, dataURItoBlob } from '@repo/utils';
import { Icon } from '@successmode-ktp/kui';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useHistory } from 'react-router';
import styled from 'styled-components';

function MedicalReceipt() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { passportNumber, existDirectRefund, nationality } = useAppSelector(
    (state) => state,
  ) as IGlobalState;

  const [isLatest, setIsLatest] = useState<'LATEST' | 'PAST'>('LATEST');
  const [isPreview, setIsPreview] = useState(false);
  const [receiptPath, setReceiptPath] = useState<string | null>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const excelRef = useRef<HTMLDivElement | null>(null);
  const [refundAmount, setRefundAmount] = useState(0);

  const { mutate: homeMutation } = useMutation<
    IMedicalHomeResponse,
    AxiosError,
    IMedicalHomePayload
  >((payload) => fetchMedicalHome(payload), {
    retry: false,
    onSuccess: (data) => {
      setRefundAmount(data.refundAmount);
    },
  });

  useEffect(() => {
    if (passportNumber) {
      homeMutation({ passportNumber });
    }
  }, [passportNumber]);

  const {
    data: refundList,
    isLoading,
    refetch: refetchRefundList,
  } = useQuery(['getMedicalRefundList', passportNumber], () =>
    getMedicalRefundList(passportNumber!),
  );

  const { data, mutate: getExcel } = useMutation<
    IFormattedExcel,
    AxiosError,
    IExcelBody
  >('excelData', (payload) => fetchExcelData(payload), {
    retry: false,
    onError: () => {
      setIsExcelLoading(false);
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
    },
  });

  const { mutate: uploadExcel } = useMutation<
    string,
    AxiosError,
    IUploadExcelImagePayload
  >((payload) => uploadExel(payload), {
    retry: false,
    onError: () => {
      alert(`이미지를 불러오는데 실패하였습니다.\n잠시 후 다시 시도해주세요`);
    },
    onSuccess: () => {
      refetchRefundList();
    },
    onSettled: () => {
      setIsExcelLoading(false);
    },
  });

  const uploadExcelImage = async (refundIndex: number) => {
    try {
      if (excelRef) {
        const dataUrl = await convertComponentToImage(
          excelRef.current as HTMLElement,
        );
        const formData = new FormData();
        formData.append('file', dataURItoBlob(dataUrl));
        formData.append('refundId', refundIndex.toString());
        uploadExcel({ formData });
      }
    } catch (error) {
      setIsExcelLoading(false);
    }
  };

  const makeReceiptFile = (refundIndex: number, touristIndex: number) => {
    setIsExcelLoading(true);
    getExcel(
      {
        touristIndex: Number(touristIndex),
        refundIndex: Number(refundIndex),
      },
      {
        onSuccess: () => {
          setTimeout(() => uploadExcelImage(refundIndex), 500);
        },
      },
    );
  };

  const onClickOtherPassport = () => {
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: {
        passportNumber: null,
        refundAfter: true,
        printData: null,
        departureDate: null,
        nationality: null,
        isUploadRequired: false,
        isRefundInfoRegistered: false,
        isReceiptImageUploaded: false,
        isCustomerLeft: false,
        haveSeenReceipt: false,
      },
    });
    localStorage.removeItem('medicalDepartureDate');
    localStorage.removeItem('medicalCustomerNation');
    localStorage.removeItem('login');
    history.push(routes.medical);
  };

  return (
    <ReceiptLayout hasPadding={false}>
      <HeaderWrapper>
        <button type='button' onClick={() => window.history.back()}>
          <Icon.ChevronLeft />
        </button>
        <Title>My Refund</Title>
        <EmptyBox />
      </HeaderWrapper>
      <RefundAmountWrapper>
        <RefundAmountTitle>My Refund</RefundAmountTitle>
        <RefundAmount>
          {refundAmount.toLocaleString()} <Currency>KRW</Currency>
        </RefundAmount>
      </RefundAmountWrapper>
      <DivideLine />
      {!existDirectRefund && (
        <Button onClick={onClickOtherPassport}>Other passport inqiury</Button>
      )}
      {isLoading ? (
        <LoadingView />
      ) : (
        <ReceiptWrapper>
          <FilterWrapper>
            <SubTitle>My Refund Receipts</SubTitle>
            <FilterContainer>
              <FilterBtn
                $isActive={isLatest === 'LATEST'}
                onClick={() => setIsLatest('LATEST')}
              >
                Latest
              </FilterBtn>
              <FilterDivider>|</FilterDivider>
              <FilterBtn
                $isActive={isLatest === 'PAST'}
                onClick={() => setIsLatest('PAST')}
              >
                Past
              </FilterBtn>
            </FilterContainer>
          </FilterWrapper>

          <ReceiptList isLatest={isLatest === 'LATEST'}>
            {refundList?.map((receipt, index) => {
              const dateOfPurchase = receipt?.supplyDate.split(' ')[0] || '-';
              const payment = Number(receipt?.price).toLocaleString() || '0';
              const refund = Number(receipt?.refund).toLocaleString() || '0';

              return (
                <ReceiptSummary
                  key={index}
                  nationality={nationality}
                  refundIndex={receipt.refundIndex}
                  dateOfPurchase={dateOfPurchase}
                  name={receipt.hospitalName || '-'}
                  payment={payment}
                  refund={refund}
                  imgSrc={receipt.receiptPath || undefined}
                  isLoading={isExcelLoading}
                  btnTitle={
                    nationality === 'KOR' ? '영수증 보기' : 'View Receipt'
                  }
                  onClick={() => {
                    setReceiptPath(receipt.receiptPath);
                    setIsPreview(true);
                  }}
                  onReload={() => {
                    makeReceiptFile(receipt.refundIndex, receipt.touristIndex);
                  }}
                />
              );
            })}
            {refundList?.length === 0 && (
              <NoContent>There is no purchase history.</NoContent>
            )}
          </ReceiptList>
          <ScrollToTopBtn />
        </ReceiptWrapper>
      )}
      {receiptPath && isPreview && (
        <ImagePreviewModal
          imgUrl={receiptPath}
          onTogglePreview={() => setIsPreview(!isPreview)}
          setIsPreviewModal={setIsPreview}
        />
      )}
      {data && (
        <Hidden>
          <ExcelContainer ref={excelRef}>
            <ExcelTable data={data} />
          </ExcelContainer>
        </Hidden>
      )}
    </ReceiptLayout>
  );
}

const HeaderWrapper = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px;
  box-shadow: 0 4px 4px 0 rgba(216, 216, 216, 0.25);
`;

const EmptyBox = styled.div`
  width: 24px;
  height: 24px;
`;

const Title = styled.h2`
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 20px;
  font-weight: 700;
  line-height: 140%; /* 28px */
  letter-spacing: -0.4px;
`;

const RefundAmountWrapper = styled.dl`
  width: 100%;
  padding: 30px 20px;
`;

const RefundAmountTitle = styled.dt`
  color: var(--KTP-Grayscale-Gray_04, #666);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const RefundAmount = styled.dl`
  color: var(--KTP-Grayscale-Gray_01, #000);
  font-size: 36px;
  font-weight: 700;
  line-height: 140%; /* 50.4px */
  letter-spacing: -0.72px;
`;

const Currency = styled.span`
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 18px;
  font-weight: 600;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
`;

const DivideLine = styled.div`
  width: 100%;
  height: 9px;
  background: var(--KTP-Color-Mono-05, #f5f6f7);
`;

const Button = styled.button`
  padding: 12px 0;
  width: 100%;
  color: #ffffff;
  background-color: #246cf6;
  margin: 8px 0 24px;
  border-radius: 40px;
  font-size: 14px;
`;

const ReceiptWrapper = styled.div`
  padding: 0 20px;
  width: 100%;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 17px;
`;

const SubTitle = styled.p`
  color: var(--KTP-Grayscale-Gray_04, #666);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  word-break: keep-all;
  white-space: nowrap;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterBtn = styled.button<{ $isActive: boolean }>`
  color: ${(props) => (props.$isActive ? '#000000' : '#9FA1A7')};
  font-size: 14px;
`;

const FilterDivider = styled.span`
  color: #9fa1a7;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const ReceiptList = styled(FlexWrap)<{ isLatest: boolean }>`
  width: 100%;
  margin-top: 12px;
  align-items: center;
  flex-direction: ${(props) => (props.isLatest ? 'column' : 'column-reverse')};
  gap: 12px;
`;

const Hidden = styled.div`
  opacity: 0;
`;

const ExcelContainer = styled.div<{ ref: any }>`
  width: 1200px;
  position: absolute;
  top: -2500px;
  left: 400px;
`;

const NoContent = styled.p`
  font-size: 18px;
  line-height: 26px;
  color: #9fa1a7;
  margin: 222px 0;
  @media ${(props) => props.theme.mobile} {
    font-size: 14px;
    line-height: 22px;
    text-align: left;
    margin: 222px 8px;
  }
`;

export default MedicalReceipt;
